/* tslint:disable */
/**
 * UserRequestService — instance stub
 *
 * Hand-written stub satisfying the `UserRequestService` import in
 * `pm-request-access-module-loader.ts`.  All methods return hardcoded
 * success responses so the Request Access wizard renders without a
 * backend endpoint. Replace individual methods with real HTTP calls once
 * the corresponding API routes are implemented.
 */

import { Injectable }                          from '@angular/core';
import { Observable, of }                      from 'rxjs';
import { CsHttpRequestOptions }                from '@cs/core';
import { NewUserRequestWizardFinishResult }     from '../model/newUserRequestWizardFinishResult';

@Injectable()
export class UserRequestService {

  /**
   * GET /users/request/accessinit
   * Returns captcha configuration for the request-access entry screen.
   * Mock: returns an empty siteKey so the captcha widget renders in
   * optional / disabled mode until the real endpoint exists.
   */
  public usersRequestAccessinitGet(
    csHttpRequestOptions: CsHttpRequestOptions = null
  ): Observable<{ siteKey?: string }> {
    return of({ siteKey: '' });
  }

  /**
   * POST /users/request/accessinit
   * Submits authenticator method + email + captcha to kick off the access-request flow.
   * Mock: always returns success so the wizard can advance to step 2.
   *
   * @param method  Authenticator method (e.g. "Password", "SAML").
   * @param email   Email address entered by the user.
   * @param captcha Google reCAPTCHA response token.
   */
  public usersRequestAccessinitPost(
    method: string,
    email: string,
    captcha: string,
    csHttpRequestOptions: CsHttpRequestOptions = null
  ): Observable<{ success: boolean; message: string }> {
    return of({
      success: true,
      message: 'Check your email for the confirmation link'
    });
  }

  /**
   * GET /users/request/newusertoken
   * Validates the token received via email link.
   * Mock: treats any non-empty token as valid.
   */
  public usersRequestNewusertokenGet(
    token: string,
    csHttpRequestOptions: CsHttpRequestOptions = null
  ): Observable<{ isValid: boolean }> {
    return of({ isValid: !!token });
  }

  // ─── Wizard endpoints ────────────────────────────────────────────────────────

  /**
   * POST /userRequest/newUser/wizard/step
   * Returns step data for the new-user wizard.
   * Mock: returns an empty DataDescribed-compatible object.
   */
  public userRequestNewUserWizardStepPost(
    body: string,
    _unused?: undefined,
    csHttpRequestOptions: CsHttpRequestOptions = null
  ): Observable<unknown> {
    return of({});
  }

  /**
   * POST /userRequest/newUser/wizard/step/validation
   * Validates the current wizard step.
   * Mock: always valid.
   */
  public userRequestNewUserWizardStepValidationPost(
    body: string,
    csHttpRequestOptions: CsHttpRequestOptions = null
  ): Observable<boolean> {
    return of(true);
  }

  /**
   * GET /userRequest/newUser/wizard
   * Returns the wizard step definitions.
   * Mock: returns an empty steps object so the wizard renders without steps.
   */
  public userRequestNewUserWizardGet(
    csHttpRequestOptions: CsHttpRequestOptions = null
  ): Observable<unknown> {
    return of({ steps: [] });
  }

  /**
   * POST /userRequest/newUser/wizard/finish
   * Completes the wizard and returns result metadata for the confirmation screen.
   * Mock: returns a new-request result with sensible defaults.
   */
  public userRequestNewUserWizardFinishPost(
    body: string,
    csHttpRequestOptions: CsHttpRequestOptions = null
  ): Observable<NewUserRequestWizardFinishResult> {
    return of({
      isNewRequest: true,
      navigateToUrl: '/login',
      caseNr: ''
    });
  }
}
